import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Lock as LockIcon,
  ContactSupport as ContactIcon,
  Link as LinkIcon,
  PhoneAndroid as SetupIcon,
  Login as LoginIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

interface AuthenticatorRequiredScreenProps {
  onBack: () => void;
}

export const AuthenticatorRequiredScreen: React.FC<AuthenticatorRequiredScreenProps> = ({
  onBack,
}) => {
  const steps = [
    {
      icon: <ContactIcon />,
      primary: 'Contacte al supervisor de su empresa',
      secondary: 'Solicite que le genere un enlace de recuperación desde Configuración → Seguridad 2FA.',
    },
    {
      icon: <LinkIcon />,
      primary: 'Reciba el enlace por correo',
      secondary: 'Le llegará un correo con un enlace de un solo uso, válido hasta 72 horas.',
    },
    {
      icon: <SetupIcon />,
      primary: 'Configure su aplicación autenticadora',
      secondary: 'Abra el enlace y escanee el código QR con Google Authenticator, Authy u otra aplicación similar.',
    },
    {
      icon: <LoginIcon />,
      primary: 'Vuelva a iniciar sesión',
      secondary: 'Una vez configurada la aplicación, podrá ingresar con normalidad usando el código de 6 dígitos.',
    },
  ];

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" align="center" fontWeight="bold">
                Configuración Requerida
              </Typography>
            </Box>

            {/* Alert */}
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Su empresa exige autenticación por aplicación (Google Authenticator, Authy, etc.)
                y el período de gracia para configurarla ya terminó. Para volver a ingresar siga estos pasos.
              </Typography>
            </Alert>

            {/* Instructions */}
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              ¿Qué debe hacer?
            </Typography>

            <List disablePadding>
              {steps.map((step, index) => (
                <ListItem
                  key={index}
                  sx={{
                    pl: 0,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      mt: 0.5,
                      color: (theme) => theme.palette.background.primary,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: (theme) => `${theme.palette.background.primary}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: (theme) => theme.palette.background.primary }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="medium">
                        {step.primary}
                      </Typography>
                    }
                    secondary={step.secondary}
                  />
                </ListItem>
              ))}
            </List>

            {/* Back Button */}
            <Box sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onBack}
                startIcon={<BackIcon />}
                sx={{
                  py: 1.5,
                  bgcolor: (theme) => theme.palette.background.buttonBg,
                  color: (theme) => theme.palette.background.buttonText,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    filter: 'brightness(0.9)',
                  },
                }}
              >
                Volver al inicio de sesión
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AuthenticatorRequiredScreen;
